import axios from 'axios';
import { getReq, putReq, postReq, deleteReq, Pagination } from './api';
import { Agency, AgencyGroup, AgencyData, AgencyQuestion } from '../utils/types'

export const getAgencies = async (
  accessToken: string
): Promise<{ data?: Agency[]; error?: string }> => {
  return getReq<Agency[]>('/agencies', accessToken);
};

export const searchAgencies = async (params: {
  accessToken: string;
  limit?: number;
  offset?: number;
  query?: string;
  kinds?: string[];
}): Promise<{ data?: AgencyData[]; pagination?: Pagination; error?: string; errors?: string[] }> => {
  const { accessToken, limit = 25, offset = 0, query, kinds } = params;

  const searchParams = new URLSearchParams();
  searchParams.append('limit', String(limit));
  searchParams.append('offset', String(offset));
  if (query?.length) searchParams.append('query', query);
  if (kinds?.length) searchParams.append('kind', kinds.join(','));

  return getReq<AgencyData[]>(
    `/agencies/search?${searchParams.toString()}`,
    accessToken,
    'Failed to fetch agencies.'
  );
};

// The states that have agencies for public registration, in display order.
// "Other" lists the agencies that have no state yet.
export const getAgencyDirectoryStates = async (): Promise<{ data?: string[]; error?: string }> => {
  return getReq<string[]>('/agency_directory/states', undefined, 'Failed to load states.');
};

// The names of the agencies in a state, as listed by getAgencyDirectoryStates.
export const getAgencyDirectoryAgencies = async (
  state: string
): Promise<{ data?: string[]; error?: string }> => {
  const stateParam = encodeURIComponent(state);
  return getReq<string[]>(`/agency_directory?state=${stateParam}`, undefined, 'Failed to load agencies.');
};

// Every state an agency can be assigned to, including territories and "Federal".
export const getAgencyStateOptions = async (): Promise<{ data?: string[]; error?: string }> => {
  return getReq<string[]>('/agency_state_options', undefined, 'Failed to load states.');
};

export const getAgency = async (
  accessToken: string,
  agencyId: number | string
): Promise<{ data?: Agency; error?: string }> => {
  return getReq<Agency>(`/agencies/${agencyId}`, accessToken);
};

const getAgencyQuestions = async (
  accessToken: string,
  agencyId: number | string
): Promise<{ data?: AgencyQuestion[], error?: string }> => {
  return getReq<AgencyQuestion[]>(`/agencies/${agencyId}/agency_questions`, accessToken);
};

export const createAgencyQuestion = async (
  accessToken: string,
  agencyId: number | string,
  agencyQuestionData: Partial<AgencyQuestion>
): Promise<{ data?: AgencyQuestion, error?: string }> => {
  return postReq<AgencyQuestion>(`/agencies/${agencyId}/agency_questions`, accessToken, agencyQuestionData);
};

export const deleteAgencyQuestion = async (
  accessToken: string,
  agencyQuestionId: string
): Promise<{ error?: string}> => {
  return deleteReq(`/agency_questions/${agencyQuestionId}`, accessToken)
}

export const getAgencyGroups = async (accessToken: string, agencyId: number) => {
  try {
    const response = await axios.get<{ data: AgencyGroup[] }>(
      `/api/v2/agencies/${agencyId}/groups`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`
        }
      }
    );

    return { data: response.data.data };
  } catch (error: any) {
    return { error: error.message || 'An unkown error occured' };
  }
};

export const updateAgency = async (
  accessToken: string,
  agencyUpdates: Partial<Agency>
): Promise<{ data?: Agency; error?: string }> => {
  return putReq<Agency>(
    `/agencies/${agencyUpdates.id}`,
    accessToken,
    {agency: agencyUpdates},
    'Failed to update agency.'
  );
};
export const createAgency = async (
  accessToken: string,
  agencyData: Record<"agency", Partial<AgencyData>>
): Promise<{ data?: Agency; error?: string }> => {
  return postReq<Agency>(
    '/agencies',
    accessToken,
    agencyData,
    'Failed to update agency.'
  );
};

export const AgenciesService = {
  getAgencyQuestions
};
